import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type SortDirection = 'ASC' | 'DESC' | null;

export interface PreviousReportFilters {
  colors?: string[];
  isRecentSigned?: true;
  recentSignedSortBy?: 'SIGNED_DATE' | 'DATE_OF_SERVICE';
  isDownloaded?: true;
  /** Pending physician review. The list query does not filter on this until the API accepts it. */
  isNonPractitionerSigned?: true;
}

interface CurrentReportFilters {
  colors?: string[];
  isNonPractitionerSigned?: true;
  isPendingReview?: true;
}

interface PatientListFiltersState {
  searchValue: string | undefined;
  previousReportFilters: PreviousReportFilters;
  currentReportFilters: CurrentReportFilters;
  sortBy: string;
  sortDirection: SortDirection;
  currentPage: number;
  pageLimit: number;
}

const initialState: PatientListFiltersState = {
  searchValue: undefined,
  previousReportFilters: {},
  currentReportFilters: {},
  sortBy: '',
  sortDirection: null,
  currentPage: 1,
  pageLimit: 10
};

const patientListFiltersSlice = createSlice({
  name: 'patientListFilters',
  initialState,
  reducers: {
    setSearchValue: (state, action: PayloadAction<string | undefined>) => {
      state.searchValue = action.payload;
      state.currentPage = 1;
    },
    setPreviousReportFilters: (state, action: PayloadAction<PreviousReportFilters>) => {
      state.previousReportFilters = action.payload;
      state.currentPage = 1;
    },
    setCurrentReportFilters: (state, action: PayloadAction<CurrentReportFilters>) => {
      state.currentReportFilters = action.payload;
      state.currentPage = 1;
    },
    clearPreviousReportFilters: (state) => {
      state.previousReportFilters = {};
      state.currentPage = 1;
    },
    clearCurrentReportFilters: (state) => {
      state.currentReportFilters = {};
      state.currentPage = 1;
    },
    setPageLimit: (state, action: PayloadAction<number>) => {
      state.pageLimit = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<{ sortBy: string; sortDirection: SortDirection }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortDirection = action.payload.sortDirection;
      state.currentPage = 1;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    resetFilters: (state) => {
      Object.assign(state, initialState);
    }
  }
});

export const {
  setSearchValue,
  setPreviousReportFilters,
  setCurrentReportFilters,
  clearPreviousReportFilters,
  clearCurrentReportFilters,
  setSort,
  setCurrentPage,
  setPageLimit,
  resetFilters
} = patientListFiltersSlice.actions;

export default patientListFiltersSlice.reducer;
